import React from 'react';
import { useSYS4110_R01Store } from './Store_SYS4110_R01';
import { CalendarDto } from 'src/dtos/calendar/calendarDto';
import { dateUtils } from './utils/calendarUtils';

interface CalendarProps {
  onDateClick?: (date: Date) => void;
  onEventClick?: (event: CalendarDto) => void;
  onTodayClick?: () => void;
}

// 요일 헤더 컴포넌트
const WeekdayHeader: React.FC = () => {
  const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
  
  return (
    <div className="grid grid-cols-7 border-b border-gray-200 ">
      {weekdays.map((day, index) => (
        <div 
          key={day} 
          className={`pt-1 pb-1  text-center text-sm font-semibold border-r border-gray-200 ${
            index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : 'text-gray-700'
          }`}
        >
          {day}
        </div>
      ))}
    </div>
  );
};

// 날짜 셀 컴포넌트
interface DateCellProps {
  date: Date;
  events: CalendarDto[];
  isCurrentMonth: boolean;
  isToday: boolean;
  dayIndex: number;
  onDateClick: (date: Date) => void;
  onEventClick: (event: CalendarDto, e: React.MouseEvent) => void;
}

const DateCell: React.FC<DateCellProps> = ({
  date,
  events,
  isCurrentMonth,
  isToday,
  dayIndex,
  onDateClick,
  onEventClick
}) => {
  const getDateClassName = () => {
    if (isToday) return 'bg-blue-600 text-white font-semibold';
    if (!isCurrentMonth) return 'text-gray-400';
    
    // 주말 색상 (일요일: 0, 토요일: 6)
    if (dayIndex % 7 === 0) return 'text-red-600 font-medium'; // 일요일
    if (dayIndex % 7 === 6) return 'text-blue-600 font-medium'; // 토요일
    return 'text-gray-900 font-medium';
  };

  const getCellClassName = () => {
    return `border-r border-b border-gray-200 h-[118px] p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
      !isCurrentMonth ? 'bg-gray-50' : ''
    }`;
  };

  return (
    <div
      className={getCellClassName()}
      onClick={() => onDateClick(date)}
    >
      {/* 날짜 숫자 */}
      <div className="flex justify-center mb-1 ">
        <span className={`w-7 h-4 flex items-center justify-center text-sm rounded-full ${getDateClassName()}`}>
          {date.getDate()}
        </span>
      </div>

      {/* 이벤트 목록 */}
      <div className="space-y-1  ">
        {events.filter(event => event.holidayYn === 'Y').slice(0, 3).map((event, idx) => (
          <div
            key={`${event.compCd}-${event.systemDay}-${idx}`}
            className="text-xs pl-2 rounded cursor-pointer bg-red-100 text-red-700 font-semibold hover:bg-red-200 transition-colors"
            onClick={(e) => onEventClick(event, e)}
            title={`${event.holidayName}${event.holidayType ? ` (${event.holidayType})` : ''}${event.remark ? ` - ${event.remark}` : ''}`}
          >
            <div className="truncate">
              {event.holidayName}
            </div>
            {event.remark && (
              <div className="text-gray-500 text-xs truncate">[{event.remark}]</div>
            )}
          </div>
        ))}
        {events.filter(event => event.holidayYn === 'Y').length > 3 && (
          <div className="text-xs p-1 text-gray-500 font-medium cursor-pointer hover:text-gray-700 transition-colors">
            ... 더보기 ({events.filter(event => event.holidayYn === 'Y').length - 3}개)
          </div>
        )}
      </div>
    </div>
  );
};

// 네비게이션 버튼 컴포넌트
interface NavigationButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}

const NavigationButton: React.FC<NavigationButtonProps> = ({ onClick, children, title }) => (
  <button
    onClick={onClick}
    className="p-2 rounded-md hover:bg-gray-100 transition-colors"
    title={title}
  >
    {children}
  </button>
);

export const Calendar_SYS4110_R01: React.FC<CalendarProps> = ({ 
  onDateClick, 
  onEventClick,
  onTodayClick
}) => {
  const { 
    currentDate, 
    events, 
    getEventsForDate,
    setSelectedDate,
    setEventModalOpen,
    navigateToNextMonth,
    navigateToPrevMonth
  } = useSYS4110_R01Store();

  const calendarDates = dateUtils.generateCalendarDates(currentDate);
  const today = new Date();
  const currentMonth = currentDate.getMonth();

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    onDateClick?.(date);
  };

  const handleEventClick = (event: CalendarDto, e: React.MouseEvent) => {
    e.stopPropagation();
    onEventClick?.(event);
  };

  return (
    <div className="flex flex-col h-full bg-white border ">
      {/* 날짜 네비게이션 */}
      <div className="flex items-center justify-center gap-4 p-2 flex-shrink-0" >
        <NavigationButton onClick={navigateToPrevMonth} title="이전 달">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </NavigationButton>
        
        <h1 className="text-xl font-semibold text-gray-800 ">
          {dateUtils.formatCurrentDate(currentDate)}
        </h1>
        
        <NavigationButton onClick={navigateToNextMonth} title="다음 달">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </NavigationButton>
      </div>

      {/* 요일 헤더 */}
      <div className="flex-shrink-0 border-t ">
        <WeekdayHeader />
      </div>

      {/* 날짜 그리드 */}
      <div className="grid grid-cols-7 flex-1 min-h-0">
        {calendarDates.map((date, index) => {
          const dayEvents = getEventsForDate(date);
          const isCurrentMonthDate = dateUtils.isCurrentMonth(date, currentMonth);
          const isTodayDate = dateUtils.isToday(date);
          
          return (
            <DateCell
              key={index}
              date={date}
              events={dayEvents}
              isCurrentMonth={isCurrentMonthDate}
              isToday={isTodayDate}
              dayIndex={index}
              onDateClick={handleDateClick}
              onEventClick={handleEventClick}
            />
          );
        })}
      </div>
    </div>
  );
}; 